// The one stop a recipient is allowed to know: their own.
//
// A tracking link is 78 bytes of capability and nothing else, which is
// what makes it safe to send by SMS — but it is also why the customer's
// page could never say when the van would arrive. §9's estimate is
// computed on the recipient's own device from the run's broadcast
// position, and it needs to know *where the recipient is*. The run does
// not carry that: a plan never goes on the wire (§20.8), only an 8-byte
// planRef, and it must stay that way. A page that learned the whole plan
// would learn every other customer's doorstep.
//
// So the dispatcher tells each recipient one thing — the stop that is
// already theirs. They know their own address; hearing it back is not a
// disclosure. It rides in the **fragment**, beside the capability and
// under the same rule: browsers never transmit a fragment, so no host,
// CDN or landing page ever receives it. It is not on the wire, not in a
// query string, and not in anybody else's copy of the link.
//
// Eleven bytes, fifteen characters. §20.2 already described the estimate
// this unlocks and called it a lower bound, which is exactly what it is:
// routing straight from the van to this door ignores the deliveries in
// between, and those can only ever make it later. `no-earlier-than` is a
// true sentence; "arriving 21:34" would not be.

/** A recipient's own stop, as carried in their copy of the link. */
export interface RecipientStop {
  /** 1-based position in the run's plan — what the outcome map is keyed by. */
  stopIndex: number;
  lat: number;
  lon: number;
}

const VERSION = 1;
const BYTES = 11;
const E7 = 10_000_000;

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function toBase64Url(bytes: Uint8Array): string {
  let out = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const a = bytes[index]!;
    const b = bytes[index + 1];
    const c = bytes[index + 2];
    out += ALPHABET[a >> 2];
    out += ALPHABET[((a & 3) << 4) | ((b ?? 0) >> 4)];
    if (b === undefined) break;
    out += ALPHABET[((b & 15) << 2) | ((c ?? 0) >> 6)];
    if (c === undefined) break;
    out += ALPHABET[c & 63];
  }
  return out;
}

function fromBase64Url(text: string): Uint8Array | null {
  const values: number[] = [];
  for (const character of text) {
    const value = ALPHABET.indexOf(character);
    if (value < 0) return null;
    values.push(value);
  }
  const out: number[] = [];
  for (let index = 0; index < values.length; index += 4) {
    const a = values[index]!;
    const b = values[index + 1];
    const c = values[index + 2];
    const d = values[index + 3];
    if (b === undefined) return null;
    out.push(((a << 2) | (b >> 4)) & 0xff);
    if (c === undefined) break;
    out.push(((b << 4) | (c >> 2)) & 0xff);
    if (d === undefined) break;
    out.push(((c << 6) | d) & 0xff);
  }
  return new Uint8Array(out);
}

/** Rounds to the E7 grid the protocol uses everywhere else, then bounds-checks. */
function coordinate(value: number, limit: number): number | null {
  if (!Number.isFinite(value)) return null;
  const scaled = Math.round(value * E7);
  return Math.abs(scaled) <= limit * E7 ? scaled : null;
}

/**
 * Encodes a recipient's own stop. Throws rather than emitting a card that
 * would decode to a different doorstep — a silently wrong stop is a
 * customer watching the wrong arrival.
 */
export function encodeRecipientStop(stop: RecipientStop): string {
  const index = Math.floor(Number(stop.stopIndex));
  if (!Number.isInteger(index) || index < 1 || index > 0xffff) {
    throw new Error(`A stop index is 1..65535; this one is ${stop.stopIndex}.`);
  }
  const latE7 = coordinate(stop.lat, 90);
  const lonE7 = coordinate(stop.lon, 180);
  if (latE7 == null || lonE7 == null) {
    throw new Error(`That stop is not a point on Earth: ${stop.lat}, ${stop.lon}.`);
  }
  const bytes = new Uint8Array(BYTES);
  const view = new DataView(bytes.buffer);
  view.setUint8(0, VERSION);
  view.setUint16(1, index);
  view.setInt32(3, latE7);
  view.setInt32(7, lonE7);
  return toBase64Url(bytes);
}

/** Decodes a card, or null for anything that is not exactly one. */
export function decodeRecipientStop(text: string): RecipientStop | null {
  const bytes = fromBase64Url(String(text ?? ""));
  if (!bytes || bytes.length !== BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint8(0) !== VERSION) return null;
  const stopIndex = view.getUint16(1);
  if (stopIndex < 1) return null;
  const lat = view.getInt32(3) / E7;
  const lon = view.getInt32(7) / E7;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { stopIndex, lat, lon };
}

/**
 * The separator between capability and card inside one fragment.
 *
 * `~` is unreserved in a URI and outside the base64url alphabet, so it
 * cannot appear in either half and splitting is unambiguous. A link with
 * no card is byte-identical to what it was before this existed — the
 * separator only appears when there is something after it.
 */
export const RECIPIENT_SEPARATOR = "~";
